/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
require('./bootstrap');

import 'lazysizes';
import { gsap } from 'gsap';
import Headroom from 'headroom.js';
import fitty from 'fitty';
import { debounce } from 'lodash';
import Rellax from 'rellax';
import Router from './router';
import { animate } from 'popmotion';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import SplitText from '../../resources/js/gsap-bonus/SplitText';
import imagesLoaded from 'imagesloaded';

gsap.registerPlugin(ScrollTrigger);



import Vue from 'vue';
import gsapCore from 'gsap/gsap-core';

require('./components/countdown-component');
require('./components/join-the-pack-component');
require('./components/more-stories-component');
require('./components/pitch-participation-component');
require('./components/pitches-overview-component')
require('./components/team-search-component');
require('./components/settings-component');
require('./components/winners-slider-component');
require('./components/pitches-grid-component');
require('./components/register-component');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true
            },
            // router: null,
            isMobile: false,
            // isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            invertedNavigation: false,
            headroom: null,
            openNavTl: null,
            closeNavTl: null,
            rellax: null,
            color: null,
            hideHeaderCta: null,
            menuInTransition: false,
            // darkHeader: '1',
            // showFooter: '1'
        };
    },

    watch: {
        isMobile: function (value) {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet: function (value) {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation: function (visibile) {
            EventBus.$emit('navigation-state-changed', visibile);
        }
    },

    mounted() {
        // Headroom
        this.checkHeadroom();

        this.router = new Router({
            instance: this,
            prefetch: true,
            section: this.$el.dataset.initialSection,
        });

        // Prepare instance properties.
        this.color = this.$el.dataset.initialColor;
        this.hideHeaderCta = this.$el.dataset.initialHideHeaderCta;

        // first check if fonts are loaded
        document.fonts.ready.then(() => {
            // fit font-sizes
            this.fitFonts();
            this.splitLines();
            this.initRevealTriggers();
            this.initRevealImages();
            if (document.querySelector('.rellax')) {
                imagesLoaded('.page', this.addRellax);
            }
        });

        this.detectMobile();
        window.addEventListener('resize', this.onWindowResize);

        EventBus.$on('window-resize', () => {
            this.checkHeadroom();
            this.detectMobile();
        });

        // Handle all methods that need to be run after a page change
        EventBus.$on('page-transition-enter', () => {
            this.initBeforePageTransition();
        });

        EventBus.$on('page-transition-enter-complete', () => {
            Vue.nextTick(() => {
                this.splitLines();
                this.initRevealTriggers();
                this.initRevealImages();
                if (document.querySelector('.rellax')) {
                    imagesLoaded('.page', this.addRellax);
                }
            });
        });
    },

    methods: {
        /**
         * Ignore router when this event is triggered for direct links or file downloads 
         *
         * @param  {Event} event
         * @return {Void}
         */
        ignoreRouter(event) {
            const target = event.currentTarget;
            window.location = target.href;
        },

        checkHeadroom() {
            if (this.headroom && window.innerWidth < 768) {
                return;
            }

            if (window.innerWidth < 768) {
                const header = document.querySelector('.header__side-bar');
                this.headroom = new Headroom(header);
                this.headroom.init();
                return;
            }

            if (!this.headroom) {
                return;
            }

            this.headroom.destroy();
            this.headroom = null;
        },

        fitFonts() {
            this.fitty = fitty('[data-fit-font]', {
                minSize: 18,
                maxSize: 92
            });
        },

        addRellax() {
            if (this.rellax) {
                this.rellax.refresh();
            }
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            if (viewportWidth >= 1024) {
                this.rellax = new Rellax('.rellax', {
                    center: true
                });
            }
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]')
            ];

            elements.forEach((element, index) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                // if (revealPosition) {
                //     console.log(revealPosition);
                // }

                const startPercentage = revealPosition || '90%';

                gsap.timeline({
                    scrollTrigger: {
                        trigger: element,
                        start: `top ${startPercentage}`,
                        onEnter: () => {
                            if (!revealDelay) {
                                element.classList.add('--reveal');
                                return;
                            }

                            gsap.delayedCall(revealDelay, () => {
                                element.classList.add('--reveal');
                            });
                        }
                    }
                });
            });
        },

        initRevealImages() {
            const images = [
                ...document.querySelectorAll('[data-reveal-image]')
            ];

            images.forEach((image, index) => {
                imagesLoaded('.page', () => {
                    const { revealDelay, revealManually, revealPosition } = image.dataset;
                    if (revealManually === '') {
                        return;
                    }

                    // if (revealPosition) {
                    //     console.log(revealPosition);
                    // }

                    const startPercentage = revealPosition || '60%';

                    gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: `top ${startPercentage}`,
                            onEnter: () => {
                                if (!revealDelay) {
                                    image.classList.add('--reveal');
                                    return;
                                }

                                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                                if (viewportWidth > 767) {
                                    gsap.delayedCall(revealDelay, () => {
                                        image.classList.add('--reveal');
                                    });
                                } else {
                                    image.classList.add('--reveal');
                                }
                            }
                        }
                    });
                });
            });
        },

        splitLines() {
            const selector = document.querySelectorAll('.splittext');

            for (let index = 0; index < selector.length; index++) {
                const item = selector[index];

                const { altMobileReveal } = item.dataset;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

                if (altMobileReveal === '' && viewportWidth < 768) {
                    const startPercentage = '90%';

                    ScrollTrigger.create({
                        trigger: item,
                        start: `top ${startPercentage}`,
                        onToggle: () => {
                            const mobileReveal = gsap.timeline();

                            mobileReveal.fromTo(item, { autoAlpha: 0 }, { duration: 4, autoAlpha: 1, ease: 'power4.out' });
                            mobileReveal.fromTo(item, { y: 60 }, { duration: 2, y: 0, ease: 'power4.out' }, 0);
                        }
                    });

                    continue;
                }

                // Continue with default splittext reveal logic
                gsap.set(item, { opacity: 1 });

                const split = new SplitText(item, {
                    type: 'lines',
                    linesClass: 'split-line'
                });

                for (let index = 0; index < split.lines.length; index++) {
                    const line = split.lines[index];
                    let inner = line.innerHTML;
                    inner = `<div class="split-line__inner">${inner}</div>`;
                    line.innerHTML = inner;
                    line.style.overflow = 'hidden';
                }
                gsap.set('.split-line__inner', { translateY: '100%', opacity: 0 });

                const startPercentage = '90%';

                selector.forEach((element) => {
                    ScrollTrigger.create({
                        trigger: element,
                        start: `top ${startPercentage}`,
                        onToggle: () => {
                            gsap.to(element.querySelectorAll('.split-line__inner'), { translateY: '0%', opacity: 1, duration: 0.5, stagger: 0.1, delay: 0.2 });
                        }
                    });
                });
            }
        },

        hoverLineMouseEnter(e) {
            const target = e.target.querySelector('.hover-line') || null;

            if (!target) {
                return;
            }

            gsap.set(target, {
                transformOrigin: 'right center',
            });

            const tl = gsap.timeline();
            tl.to(target, {
                scaleX: 0,
                ease: 'power4.easeOut',
                duration: 0.25,
            })
                .to(target, {
                    transformOrigin: 'left center',
                    scaleX: 1,
                    ease: 'power4.easeOut',
                    duration: 0.25,
                });
        },

        setBackgroundColor(color) {
            if (document.querySelector('.no-touchevents')) {
                const colors = ['yellow', 'green', 'pink', 'red', 'orange']
                const menuBody = this.$el.querySelector('.navigation-holder')
                menuBody.classList.add(colors[color]);
            }
        },

        removeBackgroundColor() {
            const menuBody = this.$el.querySelector('.navigation-holder')
            menuBody.className = "cleanstate";
            menuBody.classList.add('navigation-holder');

        },

        circleBounceMouseEnter(e) {
            if (!this.isMobile) {
                const circle = e.currentTarget.querySelector('.bounce-button-circle');
                const text = e.currentTarget.querySelector('.bounce-button-text');

                const circleAnim = animate({
                    from: 1,
                    to: .85,
                    type: "spring",
                    stiffness: 600,
                    damping: 7,
                    velocity: 2,
                    onUpdate: (latest) => {
                        gsap.set(circle, { scale: latest });
                    }
                })

                const textAnim = animate({
                    from: 1,
                    to: 1.15,
                    type: "spring",
                    stiffness: 600,
                    damping: 7,
                    velocity: 2,
                    onUpdate: (latest) => {
                        gsap.set(text, { scale: latest });
                    }
                })
            }
        },

        circleBounceMouseOut(e) {
            if (!this.isMobile) {
                const circle = e.currentTarget.querySelector('.bounce-button-circle');
                const text = e.currentTarget.querySelector('.bounce-button-text');

                const circleAnim = animate({
                    from: .85,
                    to: 1,
                    type: "spring",
                    stiffness: 600,
                    damping: 7,
                    velocity: 2,
                    onUpdate: (latest) => {
                        gsap.set(circle, { scale: latest });
                    }
                })

                const textAnim = animate({
                    from: 1.15,
                    to: 1,
                    type: "spring",
                    stiffness: 600,
                    damping: 7,
                    velocity: 2,
                    onUpdate: (latest) => {
                        gsap.set(text, { scale: latest });
                    }
                })
            }
        },

        circleRotateMouseEnter(e) {
            if (!this.isMobile) {
                const circle = e.currentTarget.querySelector('.button-circle__circle');
                const text = e.currentTarget.querySelector('.button-circle__text');
                const copy = e.currentTarget.querySelector('.button-circle__text-copy');

                const circleAnim = animate({
                    from: 0,
                    to: 180,
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(circle, { rotateX: latest });
                    }
                })

                const textAnim = animate({
                    from: '0%',
                    to: '-100%',
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(text, { translateY: latest });
                        gsap.set(copy, { translateY: latest });
                    }
                })
            }
        },

        circleRotateMouseOut(e) {
            if (!this.isMobile) {
                const circle = e.currentTarget.querySelector('.button-circle__circle');
                const text = e.currentTarget.querySelector('.button-circle__text');
                const copy = e.currentTarget.querySelector('.button-circle__text-copy');

                const circleAnim = animate({
                    from: 180,
                    to: 0,
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(circle, { rotateX: latest });
                    }
                })

                const textAnim = animate({
                    from: '-100%',
                    to: '0%',
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(text, { translateY: latest });
                        gsap.set(copy, { translateY: latest });
                    }
                })
            }
        },

        buttonIconMouseEnter(e) {
            if (!this.isMobile) {
                const circle = e.currentTarget.querySelector('.button--icon__icon-holder');
                const text = e.currentTarget.querySelector('.button--icon__text');
                const copy = e.currentTarget.querySelector('.button--icon__text-copy');

                const circleAnim = animate({
                    from: 1,
                    to: .85,
                    type: "spring",
                    stiffness: 600,
                    damping: 7,
                    velocity: 2,
                    onUpdate: (latest) => {
                        gsap.set(circle, { scale: latest });
                    }
                })

                const textAnim = animate({
                    from: '0%',
                    to: '-100%',
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(text, { translateY: latest });
                        gsap.set(copy, { translateY: latest });
                    }
                })
            }
        },

        buttonIconMouseOut(e) {
            if (!this.isMobile) {
                const circle = e.currentTarget.querySelector('.button--icon__icon-holder');
                const text = e.currentTarget.querySelector('.button--icon__text');
                const copy = e.currentTarget.querySelector('.button--icon__text-copy');

                const circleAnim = animate({
                    from: .85,
                    to: 1,
                    type: "spring",
                    stiffness: 600,
                    damping: 7,
                    velocity: 2,
                    onUpdate: (latest) => {
                        gsap.set(circle, { scale: latest });
                    }
                })

                const textAnim = animate({
                    from: '-100%',
                    to: '0%',
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(text, { translateY: latest });
                        gsap.set(copy, { translateY: latest });
                    }
                })
            }
        },

        arrowRotateMouseEnter(e) {
            if (!this.isMobile) {
                const arrowHolder = e.currentTarget.querySelector('.arrow-holder');
                const arrow = e.currentTarget.querySelector('.arrow-holder svg');

                const arrowAnim = animate({
                    from: 0,
                    to: -45,
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(arrow, { rotate: latest });
                    }
                })

                const arrowFillAnim = animate({
                    from: '#ffffff',
                    to: '#333333',
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(arrow, { fill: latest });
                    }
                })
            }
        },

        arrowRotateMouseOut(e) {
            if (!this.isMobile) {
                const arrowHolder = e.currentTarget.querySelector('.arrow-holder');
                const arrow = e.currentTarget.querySelector('.arrow-holder svg');

                const arrowAnim = animate({
                    from: -45,
                    to: 0,
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(arrow, { rotate: latest });
                    }
                })

                const arrowFillAnim = animate({
                    from: '#333333',
                    to: '#ffffff',
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(arrow, { fill: latest });
                    }
                })
            }
        },

        simpleButtonMouseEnter(e) {
            if (!this.isMobile) {
                const text = e.currentTarget.querySelector('.simple-button__text');
                const copy = e.currentTarget.querySelector('.simple-button__text-copy');
                const bg = e.currentTarget.querySelector('.simple-button__background');

                const textAnim = animate({
                    from: '0%',
                    to: '-100%',
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(text, { translateY: latest });
                        gsap.set(copy, { translateY: latest });
                    }
                })

                const bgXAnim = animate({
                    from: 1,
                    to: 1.2,
                    type: "spring",
                    stiffness: 800,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(bg, { scaleX: latest });
                    }
                })
                const bgYAnim = animate({
                    from: 1,
                    to: 1.1,
                    type: "spring",
                    stiffness: 800,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(bg, { scaleY: latest });
                    }
                })
            }
        },

        simpleButtonMouseOut(e) {
            if (!this.isMobile) {
                const text = e.currentTarget.querySelector('.simple-button__text');
                const copy = e.currentTarget.querySelector('.simple-button__text-copy');
                const bg = e.currentTarget.querySelector('.simple-button__background');

                const textAnim = animate({
                    from: '-100%',
                    to: '0%',
                    type: "spring",
                    stiffness: 200,
                    damping: 18,
                    onUpdate: (latest) => {
                        gsap.set(text, { translateY: latest });
                        gsap.set(copy, { translateY: latest });
                    }
                })

                const bgXAnim = animate({
                    from: 1.2,
                    to: 1,
                    type: "spring",
                    stiffness: 800,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(bg, { scaleX: latest });
                    }
                })
                const bgYAnim = animate({
                    from: 1.1,
                    to: 1,
                    type: "spring",
                    stiffness: 800,
                    damping: 20,
                    onUpdate: (latest) => {
                        gsap.set(bg, { scaleY: latest });
                    }
                })
            }
        },

        toggleNavigation() {
            if (this.menuInTransition) {
                return;
            }

            this.menuInTransition = true;

            if (!this.showNavigation) {
                console.log('OPEN NAV');

                const pageContent = this.$el.querySelector('#page-container');

                gsap.set(['.navigation__socials-item', '.navigation__background', '.navigation__patch', '.navigation__cta-holder'], { autoAlpha: 0 });

                const onEnterComplete = () => {

                }

                if (!this.openNavTl) {
                    this.openNavTl = gsap.timeline({ paused: true, onComplete: onEnterComplete });

                    this.openNavTl.to(pageContent, { duration: 1.6, x: '25%', ease: 'power4.inOut' }, 0);
                    this.openNavTl.to('.navigation__background', { duration: 0.6, autoAlpha: 1, ease: 'power4.out' }, 0);
                    this.openNavTl.to('.navigation__background', { duration: 1.6, x: '0%', ease: 'power4.inOut' }, 0);
                    this.openNavTl.to('.menu-page-overlay', { duration: 1, autoAlpha: 0.3, ease: 'power2.out' }, 0);
                    this.openNavTl.to('.navigation__socials-item', { duration: 2, autoAlpha: 1, stagger: 0.1, ease: 'power4.out' }, 0.6);
                    this.openNavTl.to('.navigation__anchor-text', {
                        duration: 1.2, y: '0%', stagger: 0.1, ease: 'power4.out', onComplete: () => {
                            this.menuInTransition = false;
                        }
                    }, 1.1);
                    this.openNavTl.to('.body-text-inner', { duration: 1.2, y: '0%', stagger: 0.1, ease: 'power4.out' }, 1.5);
                    this.openNavTl.to('.navigation__patch', {
                        duration: 0.2, autoAlpha: 1, ease: 'power4.out', onStart: () => {
                            const patchCircleAnim = animate({
                                from: '0.65',
                                to: '1',
                                type: "spring",
                                stiffness: 800,
                                damping: 20,
                                onUpdate: (latest) => {
                                    gsap.set('.navigation__patch-background-circle', { scale: latest });
                                },
                            })

                            const patchTextAnim = animate({
                                from: '1.15',
                                to: '1',
                                type: "spring",
                                stiffness: 800,
                                damping: 20,
                                onUpdate: (latest) => {
                                    gsap.set('.navigation__patch .cta-text', { scale: latest });
                                },
                            })
                        }
                    }, 1.8);
                    this.openNavTl.to('.navigation__cta-holder', { duration: 0.5, autoAlpha: 1 }, 2);
                }

                this.openNavTl.play(0);

                this.showNavigation = !this.showNavigation;
                this.invertedNavigation = true;
            } else {
                const pageContent = this.$el.querySelector('#page-container');

                const onComplete = () => {
                    this.showNavigation = !this.showNavigation;
                    this.menuInTransition = false;

                    gsap.set(
                        [
                            pageContent,
                            '.navigation__patch-background-circle',
                            '.navigation__patch .cta-text',
                            '.navigation__patch',
                            '.body-text-inner',
                            '.navigation__anchor-text',
                            '.navigation__socials-item',
                            '.menu-page-overlay',
                            '.navigation__background',
                            '.navigation__cta-holder'
                        ],
                        { clearProps: 'all' });
                }

                if (!this.closeNavTl) {

                    let menuItems = this.$el.querySelectorAll('.navigation__item');

                    this.closeNavTl = gsap.timeline({ paused: true, onComplete: onComplete });

                    this.closeNavTl.to(menuItems, { pointerEvents: 'none' }, 0);
                    this.closeNavTl.to('.navigation__cta-holder', { duration: 0.5, autoAlpha: 0 }, 0);
                    this.closeNavTl.to('.navigation__socials-item', { duration: 0.5, autoAlpha: 0, stagger: 0.1, ease: 'power4.out' }, 0);
                    this.closeNavTl.to('.navigation__anchor-text', { duration: 0.5, y: '100%', stagger: 0.1, ease: 'power4.out' }, 0);
                    this.closeNavTl.to('.body-text-inner', { duration: 0.5, y: '100%', stagger: 0.1, ease: 'power4.out' }, 0);
                    this.closeNavTl.to('.navigation__patch', { duration: 0.5, autoAlpha: 0, ease: 'power4.out' }, 0);
                    this.closeNavTl.to(pageContent, { duration: 1.6, x: '0%', ease: 'power4.inOut' }, 0.3);
                    this.closeNavTl.to('.navigation__background', {
                        duration: 1.6, x: '-100%', ease: 'power4.inOut', onStart: () => {
                            gsap.delayedCall(1.2, () => {
                                this.invertedNavigation = false;
                            })
                        }
                    }, 0.3);
                    this.closeNavTl.to('.menu-page-overlay', { duration: 1, autoAlpha: 0, ease: 'power2.out' }, 0.3);
                    this.closeNavTl.to(menuItems, { pointerEvents: 'auto' }, ">")
                }

                this.closeNavTl.play(0);
            }
        },

        onHeaderItemClick(event) {
            if (this.showNavigation) {
                const el = event.currentTarget;

                this.router.transition = 'from-menu';

                page(el.href);
            }

            const el = event.currentTarget;
            page(el.href);
        },

        onMenuItemClick(event) {
            const el = event.currentTarget;

            this.router.transition = 'from-menu';

            page(el.href);
        },

        initBeforePageTransition() {
            this.fitFonts();
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth < 768;
            this.isTablet = window.innerWidth < 1024;
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    }
});
